// UC21 — the entity file of one reporting entity (Registrar).
// per docs/shape-uc-21-registrator-entity-file.md §6 UI shape and the wireframe
// wireframes/react/src/pages/RegistratorDetalii.tsx
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { IconName } from '@egov-moldova/mud';
import {
  MudButton,
  MudIcon,
  MudInfoBox,
  MudSwitch,
  MudTab,
  MudTable,
  MudTabs,
  MudTag,
  MudTooltip,
} from '@egov-moldova/mud-react';
import {
  Breadcrumb,
  EMPTY_CELL,
  EmptyRegisterMessage,
  EmptyState,
  formatDate,
  formatNumber,
  formatTime,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import { apiErrorKey } from '../../app/api-client';
import { useRoleLabel } from '../../hooks/use-observer-roles';
import { QueryState } from '../../components/QueryState/QueryState';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { fetchHistory, type HistoryEntry } from '../../features/history/api';
import { HistoryActor } from '../../features/history/CatalogHistoryLog';
import { HistoryChanges } from '../../features/history/HistoryChanges';
import { HistoryMeta } from '../../features/history/HistoryMeta';
import {
  setAddressActive,
  setCredentialActive as patchCredentialActive,
} from '../../features/credentials/api';
import { CredentialTag } from '../../features/registrars/CredentialTag';
import { BlockTags, SITE_BLOCK_LABELS } from '../../features/blocking/BlockTags';
import { CorrectRegistrarDialog } from '../../features/registrars/CorrectRegistrarDialog';
import { DeactivationEffects } from '../../features/registrars/DeactivationEffects';
import {
  fetchRegistrarHistory,
  getRegistrar,
  latestSubmission,
  listCredentialHistory,
  listRegistrarSites,
  listRegistrarSubmissions,
  refreshFromRsud,
  rsudStatutLabel,
  setRegistrarActive,
  type CredentialHistoryEntry,
  type RegistrarHistoryEntry,
  type Site,
  type SubmissionRow,
} from '../../features/registrars/api';
import { CredentialChange } from '../registrar-admin/CredentialHistory';
import { fetchLocalities, localityLabel } from '../registrar-admin/sites-api';
import { TableFooter, usePageSize } from '../../components/TableFooter/TableFooter';
import './registrars.css';

const TABS = ['sites', 'system', 'reports', 'history'] as const;
type Tab = (typeof TABS)[number];

const HISTORY_PER_SITE = 25;

function readTab(raw: string | null): Tab {
  return TABS.find((tab) => tab === raw) ?? 'sites';
}

// One page size for every tab, back to page 1 whenever the tab changes — the
// wireframe's `pag` / `schimbaFila` (RegistratorDetalii.tsx:47-58); the size
// itself is the shared register default the reader can change (TableFooter).
function usePagedRows<T>(rows: readonly T[], resetKey: string) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('registrar-tabs');
  useEffect(() => setPage(1), [resetKey]);
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  // A page that no longer exists (rows removed under it) shows the last one.
  const current = Math.min(page, pages);
  return {
    page: current,
    setPage,
    pageSize,
    setPageSize,
    slice: rows.slice((current - 1) * pageSize, current * pageSize),
    offset: (current - 1) * pageSize,
  };
}

// One row of the Istoric tab, whichever trail it came from. The Registrar's
// own edits and its storage sites' edits are two tables with one reader: they
// are the same question — "what happened to this entity" — and the wireframe
// draws them as one list (RegistratorDetalii.tsx:571-598).
interface TrailRow {
  id: string;
  recordedAtIso: string;
  /** The location a row is about, for a storage-site row; the Registrar's own
   *  rows and its credential's need none — the page is already about them. */
  site?: string | undefined;
  /** A credential event, told in the credential card's own words. */
  credential?: CredentialHistoryEntry | undefined;
  /** The automatic delivery an edit of the Registrar applied (UC71-02): an MConnect event or an
   *  RSUD synchronisation run. Told by the row's `attribution.source`, never assumed MConnect. */
  eventId?: string | undefined;
  entry: Pick<HistoryEntry, 'changeType' | 'changes' | 'reason'>;
  attribution: {
    actorId: string;
    actorRole: string;
    source?: string | undefined;
  };
}

// What an event id on a row names, by the source that wrote the row (BUG-1-091): an MConnect
// delivery, or the RSUD synchronisation run. Any other source carries no id worth a caption.
function eventCaptionKey(source: string | undefined): string | null {
  if (source === 'MCONNECT_EVENT') return 'registrar_detail.history.event_id';
  if (source === 'RSUD_SYNC') return 'registrar_detail.history.rsud_sync_id';
  return null;
}

export function RegistrarDetailPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { idno = '', role = 'system-admin' } = useParams<{ idno: string; role: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = readTab(searchParams.get('tab'));
  const [resyncing, setResyncing] = useState(false);
  const [correcting, setCorrecting] = useState(false);

  const detail = useQuery({
    queryKey: ['admin-registrar', idno],
    queryFn: () => getRegistrar(idno),
    enabled: idno.length > 0,
    retry: false,
  });
  const registrar = detail.data;

  // The summary's "Ultimul raport" (RegistratorDetalii.tsx:312-321): one row of
  // the register, which is newest first, answers both "when" and "ever".
  const lastReport = useQuery({
    queryKey: ['admin-registrar-last-report', idno],
    queryFn: () => latestSubmission(idno),
    enabled: idno.length > 0,
    retry: false,
  });

  // The sites feed both their own tab and the history tab, which reads the
  // storage-site trail beside the Registrar's own.
  const sites = useQuery({
    queryKey: ['admin-registrar-sites', idno],
    queryFn: () => listRegistrarSites(idno),
    enabled: idno.length > 0 && (tab === 'sites' || tab === 'history'),
  });

  // The sites register's own read, under its own key, so the two screens share
  // one copy of the nomenclator (caiet UC30-05: a site sits in a locality).
  const localities = useQuery({
    queryKey: ['admin-site-localities'],
    queryFn: fetchLocalities,
    enabled: tab === 'sites',
    staleTime: 5 * 60_000,
  });

  const submissions = useQuery({
    queryKey: ['admin-registrar-submissions', idno],
    queryFn: () => listRegistrarSubmissions(idno),
    enabled: idno.length > 0 && tab === 'reports',
    retry: false,
  });

  // caiet UC21-10 — the entity's own edits: kind, name, address, MPass code,
  // activation. Until this feature they were journalled nowhere, so the tab
  // could only ever show what happened to the storage locations.
  const ownHistory = useQuery({
    queryKey: ['admin-registrar-history', idno],
    queryFn: () => fetchRegistrarHistory(idno),
    enabled: idno.length > 0 && tab === 'history',
    retry: false,
  });

  // caiet UC21-08 — the switches flipped on the system tab are part of what
  // happened to this Registrar; their trail lives in auth-service.
  const clientId = registrar?.systemCredential.configured
    ? registrar.systemCredential.clientId
    : '';
  const credentialHistory = useQuery({
    queryKey: ['admin-credential-history', clientId],
    queryFn: () => listCredentialHistory(clientId, idno),
    enabled: tab === 'history' && clientId !== '',
    retry: false,
  });

  const historyQueries = useQueries({
    queries: (sites.data ?? []).map((site) => ({
      queryKey: ['admin-history-entity', 'SITE', site.id],
      queryFn: () =>
        fetchHistory({ kinds: ['SITE'] as const, entityId: site.id, limit: HISTORY_PER_SITE }),
      enabled: tab === 'history',
      retry: false,
    })),
  });

  // caiet UC21-07 — the toasts say what the act did, in the wireframe's words
  // (RegistratorDetalii.tsx:153-175): an inactivation stops both channels and
  // keeps the data; a reactivation leaves the credentials to be switched on
  // separately.
  const setActive = useMutation({
    mutationFn: (active: boolean) => setRegistrarActive(registrar?.id ?? '', active),
    onSuccess: (_data, active) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-registrar', idno] });
      void queryClient.invalidateQueries({ queryKey: ['admin-registrars'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-registrar-history', idno] });
      toast({
        variant: active ? 'success' : 'info',
        title: active
          ? t('registrar_detail.toast.activated')
          : t('registrar_detail.toast.deactivated'),
        text: active
          ? t('registrar_detail.toast.activated_body')
          : t('registrar_detail.toast.deactivated_body'),
        closeLabel: t('common.close'),
      });
    },
    onError: (err: unknown) =>
      toast({
        variant: 'error',
        title: t('registrar_detail.toast.status_failed'),
        text: t(apiErrorKey(err, 'api.error.unknown')),
        closeLabel: t('common.close'),
      }),
  });

  // caiet UC21-08 — the AMDM kill switch on the machine credential and on one
  // admitted address. The endpoints are `PATCH /admin/credentials/:clientId/active`
  // and `/ip-state`; a SYSTEM_ADMIN has no implicit Registrar, so both name
  // this one explicitly (`registrar-scope.ts` rule 2).
  const setCredentialActive = useMutation({
    mutationFn: (active: boolean) =>
      patchCredentialActive({
        clientId: registrar?.systemCredential.clientId ?? '',
        registrarId: idno,
        active,
      }),
    onSuccess: (_data, active) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-registrar', idno] });
      void queryClient.invalidateQueries({ queryKey: ['admin-registrars'] });
      toast({
        variant: active ? 'success' : 'info',
        title: active
          ? t('registrar_detail.system.toast.activated')
          : t('registrar_detail.system.toast.deactivated'),
        text: active
          ? t('registrar_detail.system.toast.activated_body')
          : t('registrar_detail.system.toast.deactivated_body'),
        closeLabel: t('common.close'),
      });
    },
    onError: (err: unknown) =>
      toast({
        variant: 'error',
        title: t('registrar_detail.system.toast.failed'),
        text: t(apiErrorKey(err, 'api.error.unknown')),
        closeLabel: t('common.close'),
      }),
  });

  const setIpActive = useMutation({
    mutationFn: ({ address, active }: { address: string; active: boolean }) =>
      setAddressActive({
        clientId: registrar?.systemCredential.clientId ?? '',
        registrarId: idno,
        address,
        active,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-registrar', idno] });
      toast({
        variant: variables.active ? 'success' : 'info',
        title: variables.active
          ? t('registrar_detail.system.toast.ip_activated')
          : t('registrar_detail.system.toast.ip_deactivated'),
        text: variables.address,
        closeLabel: t('common.close'),
      });
    },
    onError: (err: unknown) =>
      toast({
        variant: 'error',
        title: t('registrar_detail.system.toast.failed'),
        text: t(apiErrorKey(err, 'api.error.unknown')),
        closeLabel: t('common.close'),
      }),
  });

  // RegistratorDetalii.tsx:394-400 — "Cod (MPass)", not "Cod intern": a
  // site's code IS its MPass organisation code (caiet UC30-04).
  const siteColumns = useMemo(
    () => [
      { key: 'code', label: t('registrar_detail.sites.column.code') },
      { key: 'name', label: t('registrar_detail.sites.column.name'), width: '32%' },
      { key: 'locality', label: t('registrar_detail.sites.column.locality') },
      { key: 'status', label: t('registrar_detail.sites.column.status') },
      { key: 'lastChange', label: t('registrar_detail.sites.column.last_change') },
    ],
    [t],
  );

  // RegistratorDetalii.tsx:480-484 — the format and the switch in narrow tracks.
  const ipColumns = useMemo(
    () => [
      { key: 'address', label: t('registrar_detail.system.column.address') },
      { key: 'format', label: t('registrar_detail.system.column.format'), width: '104px' },
      { key: 'state', label: t('registrar_detail.system.column.state'), width: '96px' },
    ],
    [t],
  );

  // RegistratorDetalii.tsx:527-533.
  const reportColumns = useMemo(
    () => [
      { key: 'confirmedAt', label: t('registrar_detail.reports.column.confirmed') },
      { key: 'site', label: t('registrar_detail.reports.column.site') },
      { key: 'operator', label: t('registrar_detail.reports.column.operator') },
      {
        key: 'rowCount',
        label: t('registrar_detail.reports.column.rows'),
        align: 'end' as const,
      },
    ],
    [t],
  );

  // RegistratorDetalii.tsx:574-578 — three columns. Which location a row is
  // about is written in the change cell, not in a fourth column.
  const historyColumns = useMemo(
    () => [
      { key: 'recordedAt', label: t('history.column.when'), width: '120px' },
      // The wireframe's own heading for this table, not the shared "Modificare"
      // the other history screens read.
      { key: 'change', label: t('registrar_detail.history.column.change') },
      { key: 'actor', label: t('history.column.actor'), width: '180px' },
    ],
    [t],
  );

  async function resync(): Promise<void> {
    setResyncing(true);
    try {
      const result = await refreshFromRsud(idno);
      // The check date moves even when nothing else did, so the dialog's
      // "verificată {date}" and the register's RSUD column read it again.
      await queryClient.invalidateQueries({ queryKey: ['admin-registrar', idno] });
      await queryClient.invalidateQueries({ queryKey: ['admin-registrar-history', idno] });
      void queryClient.invalidateQueries({ queryKey: ['admin-registrars'] });
      // An answer that changed nothing is still an answer: the wireframe says
      // "Resincronizat cu RSUD · Denumirea și statutul sunt la zi"
      // (RegistratorDetalii.tsx:127-131), not that there was nothing to do.
      toast({
        variant: 'success',
        title: t('registrars.rsud.resynced.title'),
        text: result.applied
          ? t('registrars.rsud.resynced.body', {
              name: result.name,
              statut: rsudStatutLabel(t, result.statut),
            })
          : t('registrar_detail.rsud.unchanged_body'),
        closeLabel: t('common.close'),
      });
    } catch (err) {
      toast({
        variant: 'error',
        title: t('registrars.rsud.failed.title'),
        text: t(apiErrorKey(err, 'registrars.rsud.failed.body')),
        closeLabel: t('common.close'),
      });
    } finally {
      setResyncing(false);
    }
  }

  if (detail.isError) {
    // "Does not exist" is a claim, and a refused read is not evidence for it.
    // A database that timed out, a service that is down and a session that
    // lapsed all reached this branch and were reported as an absent
    // Registrar — which sends the administrator looking for a record that is
    // sitting where they left it. The wireframe draws only the absent case
    // (RegistratorDetalii.tsx:73-100); the unreachable one is the constitution's
    // (article V), and it keeps its own words and a retry.
    const unreachable =
      apiErrorKey(detail.error, '') !== 'api.error.registrar_not_found' &&
      apiErrorKey(detail.error, '') !== 'api.error.invalid_idno';
    const back = (
      <MudButton
        shape="circular"
        variant="neutral"
        size="md"
        onClick={() => navigate(`/${role}/registrars`)}
      >
        {t('registrar_detail.missing.back')}
      </MudButton>
    );
    return (
      <section className="simsm-page simsm-registrar-detail">
        <Breadcrumb
          label={t('registrar_detail.breadcrumb.aria_label')}
          crumbs={[
            { label: t('registrars.title'), to: `/${role}/registrars` },
            {
              label: unreachable
                ? t('registrar_detail.unreachable.title')
                : t('registrar_detail.missing.crumb'),
            },
          ]}
        />
        {unreachable ? (
          <>
            <MudInfoBox
              emphasis="strong"
              variant="warning"
              titleText={t('registrar_detail.unreachable.title')}
            >
              {t('registrar_detail.unreachable.body')}
            </MudInfoBox>
            <div className="simsm-registrar-detail__back">
              <MudButton
                shape="circular"
                variant="primary"
                size="md"
                onClick={() => void detail.refetch()}
              >
                {t('common.retry')}
              </MudButton>
              {back}
            </div>
          </>
        ) : (
          <EmptyState
            icon="building"
            title={t('registrar_detail.missing.title')}
            body={t('registrar_detail.missing.body', { idno })}
            action={back}
          />
        )}
      </section>
    );
  }

  if (!registrar) {
    return (
      <p className="simsm-query-state__loading" role="status" aria-live="polite">
        {t('common.loading')}
      </p>
    );
  }

  const credential = registrar.systemCredential;
  // CF-48: the switch per address is the System Administrator's own block; the
  // Registrar Administrator's is named beside the address and not lifted here.
  const suspended = new Set(credential.suspendedIps);
  const suspendedByRegistrar = new Set(credential.registrarSuspendedIps ?? []);
  // IPv4 or IPv6 by the address itself, a range included, as the wireframe
  // names them (RegistratorDetalii.tsx:490).
  const ipRows = [...credential.allowedIps, ...credential.allowedIpCidrs].map((address) => ({
    id: address,
    address,
    format: address.includes(':') ? 'IPv6' : 'IPv4',
  }));
  const siteRows = sites.data ?? [];
  const localityById = new Map((localities.data ?? []).map((l) => [l.id, localityLabel(l)]));
  const submissionRows = submissions.data?.submissions ?? [];
  const siteLabels = new Map(siteRows.map((site) => [site.id, site.internalCode]));
  const trail = buildTrail(
    ownHistory.data ?? [],
    historyQueries.flatMap((query) => query.data?.entries ?? []),
    credentialHistory.data ?? [],
    siteLabels,
  );

  return (
    <section className="simsm-page simsm-registrar-detail">
      <Breadcrumb
        label={t('registrar_detail.breadcrumb.aria_label')}
        crumbs={[
          { label: t('registrars.title'), to: `/${role}/registrars` },
          { label: registrar.name },
        ]}
      />

      {/* The status belongs to the title, as the wireframe writes it
          (`{denumire} [Activ]`, RegistratorDetalii.tsx:208-215), and the
          wireframe draws no sentence under it. */}
      <PageHeader
        title={
          <>
            {registrar.name}{' '}
            <span className="simsm-registrar-detail__header-tag">
              <MudTag
                variant="status"
                semantic={registrar.active ? 'success' : 'neutral'}
                label={
                  registrar.active ? t('registrars.status.active') : t('registrars.status.inactive')
                }
              />
            </span>
          </>
        }
        actions={
          <>
            {/* caiet UC21-06 — the correction opens on the file, in the
                wireframe's dialog (RegistratorDetalii.tsx:604-668); the
                administrator never leaves the Registrar to fix its code. */}
            {/* The wireframe's `btn outlined strict md`. */}
            <MudButton
              shape="circular"
              appearance="outlined"
              variant="strict"
              size="md"
              onClick={() => setCorrecting(true)}
            >
              {t('registrars.action.edit')}
            </MudButton>
            <RowActionsMenu
              label={t('registrar_detail.actions.aria_label')}
              // Beside the header's own button, at the right edge of the page:
              // the wireframe's `ActiuniMenu size="md"`, opening leftwards.
              size="md"
              align="end"
              actions={[
                {
                  label: resyncing
                    ? t('registrars.action.rsud_resync_pending')
                    : t('registrars.action.rsud_resync'),
                  disabled: resyncing,
                  onSelect: () => void resync(),
                },
                {
                  label: t('registrar_detail.actions.manage_sites'),
                  onSelect: () => navigate(`/${role}/sites?registrar=${registrar.idno}`),
                },
                {
                  label: t('registrars.action.credentials'),
                  onSelect: () => navigate(`/${role}/credentials?registrar=${registrar.idno}`),
                },
                {
                  label: registrar.active
                    ? t('registrar_detail.actions.deactivate')
                    : t('registrar_detail.actions.activate'),
                  danger: registrar.active,
                  disabled: setActive.isPending,
                  separator: true,
                  onSelect: () => {
                    void (async () => {
                      // Only the inactivation asks (RegistratorDetalii.tsx:247):
                      // it stops a Registrar reporting; a reactivation
                      // stops nothing.
                      if (registrar.active) {
                        const ok = await confirm({
                          title: t('registrar_detail.confirm.title'),
                          body: (
                            <DeactivationEffects
                              name={registrar.name}
                              credentialActive={credential.configured && credential.active}
                              siteCount={registrar.sites.total}
                            />
                          ),
                          confirmLabel: t('registrar_detail.confirm.action'),
                          destructive: true,
                        });
                        if (!ok) return;
                      }
                      setActive.mutate(!registrar.active);
                    })();
                  },
                },
              ]}
            />
          </>
        }
      />

      {/* The wireframe raises the two states that change what an administrator
          should do next: struck off the state register, and inactivated here
          (RegistratorDetalii.tsx:272-292). */}
      {/* `strong`: the wireframe tints both boxes (`.info-box.error` on the
          danger ground, `.warning` on the amber one), where AGE's default
          `subtle` draws them grey. The title leads the sentence, on one line
          (RegistratorDetalii.tsx:272-292). */}
      {registrar.rsud.statut === 'RADIAT' ? (
        <MudInfoBox variant="error" emphasis="strong">
          <strong>{t('registrar_detail.radiat.title')}.</strong>{' '}
          {registrar.active
            ? t('registrar_detail.radiat.body_active')
            : t('registrar_detail.radiat.body_inactive')}
        </MudInfoBox>
      ) : null}
      {!registrar.active && registrar.rsud.statut !== 'RADIAT' ? (
        <MudInfoBox variant="warning" emphasis="strong">
          <strong>{t('registrar_detail.inactive.title')}.</strong>{' '}
          {t('registrar_detail.inactive.body')}
        </MudInfoBox>
      ) : null}

      <dl className="simsm-registrar-detail__summary">
        <div>
          <dt>{t('registrar_detail.field.idno')}</dt>
          <dd className="simsm-registrar-detail__identifier">{registrar.idno}</dd>
        </div>
        <div>
          <dt>{t('registrar_detail.field.rsud_statut')}</dt>
          <dd>
            {registrar.rsud.statut ? (
              <MudTag
                variant="status"
                semantic={
                  registrar.rsud.statut === 'ACTIV'
                    ? 'success'
                    : registrar.rsud.statut === 'RADIAT'
                      ? 'danger'
                      : 'accent'
                }
                label={rsudStatutLabel(t, registrar.rsud.statut)}
              />
            ) : (
              <Muted text={t('registrar_detail.field.rsud_never')} />
            )}
          </dd>
        </div>
        {/* caiet UC21-05 — the wireframe's "Cod organizație (MPass)"
            (RegistratorDetalii.tsx:306-309). Empty is a real state: no code has
            been recorded yet, and the edit form is where it is corrected. */}
        <div>
          <dt>{t('registrar_detail.field.mpass_code')}</dt>
          <dd>
            {registrar.mpassOrgCode ? (
              <span className="simsm-registrar-detail__identifier">{registrar.mpassOrgCode}</span>
            ) : (
              <Muted text={t('registrars.mpass_code.unset')} />
            )}
          </dd>
        </div>
        <div>
          <dt>{t('registrar_detail.field.kind')}</dt>
          <dd>
            {registrar.kind ? (
              <KindLabel kind={registrar.kind} />
            ) : (
              <Muted text={t('registrar_detail.field.kind_unset')} />
            )}
          </dd>
        </div>
        <div>
          <dt>{t('registrar_detail.field.created')}</dt>
          <dd>{formatDate(registrar.createdAtIso)}</dd>
        </div>
        <div>
          <dt>{t('registrar_detail.field.last_report')}</dt>
          <dd>
            {lastReport.isError ? (
              EMPTY_CELL
            ) : lastReport.data === undefined ? (
              <Muted text={t('common.loading')} />
            ) : lastReport.data.confirmedAtIso ? (
              formatDate(lastReport.data.confirmedAtIso)
            ) : (
              <Muted text={t('registrar_detail.field.no_reports')} />
            )}
          </dd>
        </div>
        <div>
          <dt>{t('registrar_detail.field.sites')}</dt>
          <dd>
            {/* "4 active", and the inactive ones in tertiary only when there
                are any (RegistratorDetalii.tsx:326-339): "· 0 inactive" is a
                number that says nothing. */}
            {registrar.sites.total === 0 ? (
              <Muted text={t('registrar_detail.field.sites_none')} />
            ) : (
              <>
                {t('registrar_detail.field.sites_active', { count: registrar.sites.active })}
                {registrar.sites.inactive > 0 ? (
                  <span className="simsm-registrar-detail__tertiary">
                    {' · '}
                    {t('registrar_detail.field.sites_inactive', {
                      count: registrar.sites.inactive,
                    })}
                  </span>
                ) : null}
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>{t('registrar_detail.field.system')}</dt>
          <dd className="simsm-registrar-detail__tags">
            <CredentialTag
              configured={credential.configured}
              active={credential.active}
              blockedBySystemAdmin={credential.blockedBySystemAdmin}
              blockedByRegistrar={credential.blockedByRegistrar}
            />
          </dd>
        </div>
      </dl>

      {/* `sm`, measured against the wireframe's `.tab` at 1440: 14/20 like the
          drawing and 41px against its 44, where `md` sets 16/24 at 49px and
          pushes each tab 26px further along. */}
      <MudTabs
        size="sm"
        value={tab}
        aria-label={t('registrar_detail.tabs.aria_label')}
        onMudChange={(e: CustomEvent<{ value: string }>) =>
          setSearchParams({ tab: readTab(e.detail.value) }, { replace: true })
        }
      >
        {/* The count only when there is something to count
            (RegistratorDetalii.tsx:374): a "0" badge is a number that says
            nothing, and the empty tab says it better. */}
        <MudTab
          size="sm"
          value="sites"
          label={t('registrar_detail.tab.sites')}
          {...(registrar.sites.total > 0 ? { badgeCount: registrar.sites.total } : {})}
        />
        <MudTab size="sm" value="system" label={t('registrar_detail.tab.system')} />
        <MudTab size="sm" value="reports" label={t('registrar_detail.tab.reports')} />
        <MudTab size="sm" value="history" label={t('registrar_detail.tab.history')} />
      </MudTabs>

      <div className="simsm-registrar-detail__panel">
        {tab === 'sites' ? (
          <QueryState
            query={sites}
            isEmpty={siteRows.length === 0}
            empty={
              <TabEmpty icon="store">
                {t('registrar_detail.sites.empty')}{' '}
                <RouterLink
                  to={`/${role}/sites?registrar=${registrar.idno}`}
                  size="sm"
                  underline="always"
                >
                  {t('registrar_detail.sites.empty_link')}
                </RouterLink>
                .
              </TabEmpty>
            }
          >
            <SitesTable
              rows={siteRows}
              columns={siteColumns}
              tab={tab}
              localityOf={(geographyId) => localityById.get(geographyId) ?? EMPTY_CELL}
              onOpen={(site) =>
                navigate(
                  `/${role}/sites/${encodeURIComponent(site.internalCode)}/edit?registrar=${site.registrarId}`,
                )
              }
            />
          </QueryState>
        ) : null}

        {tab === 'system' ? (
          <div className="simsm-registrar-detail__system">
            {credential.configured ? (
              <>
                <div className="simsm-registrar-detail__system-bar">
                  <dl className="simsm-registrar-detail__system-summary">
                    <div>
                      <dt>
                        {t('registrar_detail.system.field.state')}
                        {/* The wireframe's own note (RegistratorDetalii.tsx:455-458):
                            the administrator switches the credential and its
                            addresses on and off, and never sets or resets the
                            secret — that is the Registrar Administrator's act
                            (UC31). */}
                        <MudTooltip
                          content={t('registrar_detail.system.field.state_tip')}
                          position="top"
                        >
                          <span
                            slot="trigger"
                            className="simsm-registrar-detail__tip"
                            role="img"
                            aria-label={t('registrar_detail.system.field.state_tip')}
                          >
                            <MudIcon name="circle-info" variant="filled" size={16} />
                          </span>
                        </MudTooltip>
                      </dt>
                      <dd>
                        <CredentialTag
                          configured={credential.configured}
                          active={credential.active}
                          blockedBySystemAdmin={credential.blockedBySystemAdmin}
                          blockedByRegistrar={credential.blockedByRegistrar}
                        />
                      </dd>
                    </div>
                    <div>
                      <dt>{t('registrar_detail.system.field.client_id')}</dt>
                      <dd className="simsm-registrar-detail__identifier">{credential.clientId}</dd>
                    </div>
                  </dl>
                  {/* caiet UC21-08 — the wireframe's "Credențiale active" switch
                      (RegistratorDetalii.tsx:462-473). An inactivated Registrar
                      cannot report at all, so its credential switch is disabled
                      with the reason rather than silently inert. The words go in
                      the `label` slot: the prop is only an accessible name. */}
                  <div className="simsm-registrar-detail__system-toggle">
                    {/* CF-48: this switch is the System Administrator's own block; the
                        Registrar Administrator's is named beside it, never lifted. */}
                    <MudSwitch
                      checked={!credential.blockedBySystemAdmin}
                      disabled={!registrar.active || setCredentialActive.isPending}
                      onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                        setCredentialActive.mutate(e.detail.checked)
                      }
                    >
                      <span slot="label">{t('registrar_detail.system.toggle.credential')}</span>
                    </MudSwitch>
                    {!registrar.active ? (
                      <span className="simsm-registrar-detail__muted">
                        {t('registrar_detail.system.toggle.blocked')}
                      </span>
                    ) : credential.blockedByRegistrar ? (
                      <span className="simsm-registrar-detail__muted">
                        {t('registrar_detail.system.toggle.blocked_by_registrar')}
                      </span>
                    ) : null}
                  </div>
                </div>

                {/* The table stands with no address in it, and says why in
                    its one row (RegistratorDetalii.tsx:503-509). */}
                <MudTable
                  className="simsm-table"
                  columns={ipColumns}
                  rows={ipRows}
                  rowStyle="zebra"
                  aria-label={t('registrar_detail.system.table_aria_label')}
                >
                  <EmptyRegisterMessage>{t('registrar_detail.system.no_ips')}</EmptyRegisterMessage>
                  {/* caiet UC21-08 — "Activă" per admitted address. Suspending
                      one is not removing it: it stays admitted, switched off. */}
                  {ipRows.map((row, index) => (
                    <span
                      key={`address-${row.id}`}
                      slot={`cell-address-${index}`}
                      className="simsm-registrar-detail__address"
                    >
                      <span className="simsm-registrar-detail__identifier">{row.address}</span>
                      {suspendedByRegistrar.has(row.address) ? (
                        <MudTag
                          variant="status"
                          type="subtle"
                          size="sm"
                          semantic="accent"
                          label={t('credentials.ips.state.blocked_by_registrar')}
                        />
                      ) : null}
                    </span>
                  ))}
                  {ipRows.map((row, index) => (
                    <span key={`state-${row.id}`} slot={`cell-state-${index}`}>
                      <MudSwitch
                        checked={!suspended.has(row.address)}
                        disabled={!registrar.active || setIpActive.isPending}
                        label={t('registrar_detail.system.toggle.ip', { address: row.address })}
                        onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                          setIpActive.mutate({ address: row.address, active: e.detail.checked })
                        }
                      />
                    </span>
                  ))}
                </MudTable>
              </>
            ) : (
              <TabEmpty icon="lock">{t('registrar_detail.system.unset.body')}</TabEmpty>
            )}
          </div>
        ) : null}

        {tab === 'reports' ? (
          submissions.isError ? (
            <MudInfoBox
              emphasis="strong"
              variant="warning"
              titleText={t('registrar_detail.reports.blocked.title')}
            >
              {t('registrar_detail.reports.blocked.body')}
            </MudInfoBox>
          ) : (
            <QueryState
              query={submissions}
              isEmpty={submissionRows.length === 0}
              empty={<TabEmpty icon="document">{t('registrar_detail.reports.empty')}</TabEmpty>}
            >
              <div className="simsm-registrar-detail__section-head">
                <span className="simsm-registrar-detail__section-count">
                  {t('registrar_detail.reports.head', { count: submissionRows.length })}
                </span>
                <RouterLink
                  to={`/${role}/reports?registrar=${registrar.idno}`}
                  size="sm"
                  underline="hover"
                >
                  {t('registrar_detail.reports.all')}
                </RouterLink>
              </div>
              <ReportsTable
                rows={submissionRows}
                columns={reportColumns}
                tab={tab}
                onOpen={(id) => navigate(`/${role}/reports/${id}`)}
              />
            </QueryState>
          )
        ) : null}

        {tab === 'history' ? (
          <HistoryPanel
            columns={historyColumns}
            rows={trail}
            tab={tab}
            ownFailed={ownHistory.isError}
            sitesFailed={historyQueries.some((query) => query.isError) || credentialHistory.isError}
            loading={ownHistory.isLoading || sites.isLoading}
            stillArriving={
              historyQueries.some((query) => query.isLoading) || credentialHistory.isLoading
            }
            hasSites={siteLabels.size > 0}
          />
        ) : null}
      </div>

      <CorrectRegistrarDialog
        open={correcting}
        registrar={registrar}
        onClose={() => setCorrecting(false)}
        onSaved={({ summary }) => {
          void queryClient.invalidateQueries({ queryKey: ['admin-registrar', idno] });
          void queryClient.invalidateQueries({ queryKey: ['admin-registrars'] });
          void queryClient.invalidateQueries({ queryKey: ['admin-registrar-history', idno] });
          toast({
            variant: 'success',
            title: t('registrars.toast.saved'),
            text: summary,
            closeLabel: t('common.close'),
          });
        }}
        onStale={() => void detail.refetch()}
      />
    </section>
  );
}

// Every trail, newest first: the Registrar's own edits, its storage sites',
// and its credential's switches. A storage-site row carries the location it
// happened to, so "this address changed" reads apart from "that location's".
function buildTrail(
  own: RegistrarHistoryEntry[],
  siteEntries: HistoryEntry[],
  credentialEntries: CredentialHistoryEntry[],
  siteLabels: ReadonlyMap<string, string>,
): TrailRow[] {
  const rows: TrailRow[] = [
    ...own.map((entry) => ({
      id: entry.id,
      recordedAtIso: entry.recordedAtIso,
      ...(entry.eventId ? { eventId: entry.eventId } : {}),
      entry: { changeType: entry.changeType, changes: entry.changes },
      attribution: {
        actorId: entry.actorId,
        actorRole: entry.actorRole,
        source: entry.source,
      },
    })),
    ...siteEntries.map((entry) => ({
      id: entry.id,
      recordedAtIso: entry.recordedAtIso,
      site: siteLabels.get(entry.entityId) ?? entry.entityLabel,
      entry: {
        changeType: entry.changeType,
        changes: entry.changes,
        ...(entry.reason ? { reason: entry.reason } : {}),
      },
      attribution: {
        actorId: entry.actorId,
        actorRole: entry.actorRole,
        source: entry.source,
      },
    })),
    ...credentialEntries.map((entry) => ({
      id: `credential-${entry.id}`,
      recordedAtIso: entry.recordedAtIso,
      credential: entry,
      entry: { changeType: entry.changeType, changes: [] },
      attribution: {
        actorId: entry.actorId,
        actorRole: entry.actorRole,
      },
    })),
  ];
  return rows.sort((a, b) => b.recordedAtIso.localeCompare(a.recordedAtIso));
}

function Muted({ text }: { text: string }): JSX.Element {
  return <span className="simsm-registrar-detail__tertiary">{text}</span>;
}

function KindLabel({ kind }: { kind: string }): JSX.Element {
  const { t } = useTranslate();
  return <>{t(`registrars.kind.${kind}`)}</>;
}

// The wireframe's `.gol-mic` (RegistratorDetalii.tsx:441-447, 510-515, 553-556):
// a tab with nothing in it says so in one tertiary line beside a small glyph,
// inside the tab, not in a card. AGE's icon sizes are 16 and 20; the
// wireframe's 18 sits between them.
function TabEmpty({ icon, children }: { icon: IconName; children: ReactNode }): JSX.Element {
  return (
    <p className="simsm-registrar-detail__empty">
      <MudIcon name={icon} size={20} />
      <span>{children}</span>
    </p>
  );
}

type Column = { key: string; label: string; width?: string; align?: 'end' };

// A date, and its time in tertiary after a comma: `3 iun., 10:12`
// (RegistratorDetalii.tsx:423-428).
function DateAndTime({ iso }: { iso: string }): JSX.Element {
  return (
    <>
      {formatDate(iso)}
      <span className="simsm-registrar-detail__tertiary">, {formatTime(iso)}</span>
    </>
  );
}

function SitesTable({
  rows,
  columns,
  tab,
  localityOf,
  onOpen,
}: {
  rows: Site[];
  columns: Column[];
  tab: string;
  localityOf: (geographyId: string) => string;
  /** The whole row opens the location, as a row of the Locații register does. */
  onOpen: (site: Site) => void;
}): JSX.Element {
  const { t } = useTranslate();
  const roleLabelFor = useRoleLabel();
  const lastChangeAuthor = (site: Site): string =>
    site.lastChangeActorId ||
    (site.lastChangeActorRole ? roleLabelFor(site.lastChangeActorRole) : '');
  const paged = usePagedRows(rows, tab);
  return (
    <div className="simsm-registrar-detail__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={paged.slice.map((site) => ({ id: site.id }))}
        rowStyle="zebra"
        aria-label={t('registrar_detail.sites.table_aria_label')}
        hoverable
        onMudRowClick={onRowOpen((index) => {
          const site = paged.slice[index];
          if (site) onOpen(site);
        })}
      >
        {paged.slice.map((site, index) => {
          // An inactive location mutes its row, the wireframe's `tr.inactiv`.
          const cell = site.active
            ? 'simsm-registrar-detail__cell'
            : 'simsm-registrar-detail__cell simsm-registrar-detail__cell--inactive';
          return (
            <Fragment key={site.id}>
              <span
                slot={`cell-code-${index}`}
                className={`${cell} simsm-registrar-detail__identifier`}
              >
                {site.internalCode}
              </span>
              <span
                slot={`cell-name-${index}`}
                className={`${cell} simsm-registrar-detail__strong`}
              >
                {site.name}
              </span>
              <span slot={`cell-locality-${index}`} className={cell}>
                {localityOf(site.geographyId)}
              </span>
              <span slot={`cell-status-${index}`} className={cell}>
                <BlockTags
                  item={site}
                  viewer="SYSTEM_ADMIN"
                  labels={SITE_BLOCK_LABELS}
                  size="sm"
                  type={site.active ? 'outlined' : 'subtle'}
                />
              </span>
              <span slot={`cell-lastChange-${index}`} className={cell}>
                {site.updatedAtIso ? (
                  <span className="simsm-registrar-detail__stacked">
                    <span>
                      <DateAndTime iso={site.updatedAtIso} />
                    </span>
                    {/* The catalogue stores no editor name (BUG-1-009): the masked id
                        names the author, the role label when the row has no id, and the
                        date stands alone when the row has neither (UC30-07). */}
                    {lastChangeAuthor(site) ? (
                      <HistoryMeta
                        label={t('registrar_detail.sites.modified_by')}
                        value={lastChangeAuthor(site)}
                      />
                    ) : null}
                  </span>
                ) : (
                  <span className="simsm-registrar-detail__tertiary">{EMPTY_CELL}</span>
                )}
              </span>
            </Fragment>
          );
        })}
      </MudTable>
      <TableFooter
        total={rows.length}
        page={paged.page}
        pageSize={paged.pageSize}
        onPage={paged.setPage}
        onPageSize={paged.setPageSize}
      />
    </div>
  );
}

function ReportsTable({
  rows,
  columns,
  tab,
  onOpen,
}: {
  rows: SubmissionRow[];
  columns: Column[];
  tab: string;
  onOpen: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslate();
  const paged = usePagedRows(rows, tab);
  return (
    <div className="simsm-registrar-detail__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={paged.slice.map((row) => ({
          id: row.id,
          site: row.siteName || EMPTY_CELL,
          operator: row.operatorName || EMPTY_CELL,
          rowCount: formatNumber(row.rowCount),
        }))}
        rowStyle="zebra"
        hoverable
        aria-label={t('registrar_detail.reports.table_aria_label')}
        onMudRowClick={onRowOpen<{ index: number; row: { id?: string | number } }>(
          (_index, { row }) => {
            if (row.id) onOpen(String(row.id));
          },
        )}
      >
        {paged.slice.map((row, index) => (
          <span key={`confirmed-${row.id}`} slot={`cell-confirmedAt-${index}`}>
            <DateAndTime iso={row.confirmedAtIso} />
          </span>
        ))}
      </MudTable>
      <TableFooter
        total={rows.length}
        page={paged.page}
        pageSize={paged.pageSize}
        onPage={paged.setPage}
        onPageSize={paged.setPageSize}
      />
    </div>
  );
}

interface HistoryPanelProps {
  columns: Column[];
  rows: TrailRow[];
  tab: string;
  ownFailed: boolean;
  sitesFailed: boolean;
  loading: boolean;
  stillArriving: boolean;
  hasSites: boolean;
}

// A short list here is not proof that little happened: the storage-site half is
// read one location at a time and the credential's from another service, and a
// read that failed is a hole the reader has to be told about.
function HistoryPanel({
  columns,
  rows,
  tab,
  ownFailed,
  sitesFailed,
  loading,
  stillArriving,
  hasSites,
}: HistoryPanelProps): JSX.Element {
  const { t } = useTranslate();
  const paged = usePagedRows(rows, tab);

  return (
    <div className="simsm-registrar-detail__history">
      {ownFailed ? (
        <MudInfoBox
          emphasis="strong"
          variant="warning"
          titleText={t('registrar_detail.history.blocked.title')}
        >
          {t('registrar_detail.history.blocked.body')}
        </MudInfoBox>
      ) : null}
      {sitesFailed ? (
        <MudInfoBox emphasis="strong" variant="warning">
          {t('registrar_detail.history.sites_blocked')}
        </MudInfoBox>
      ) : null}
      {loading ? (
        <p className="simsm-query-state__loading" role="status" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : rows.length === 0 ? (
        <TabEmpty icon="history">
          {hasSites
            ? t('registrar_detail.history.empty')
            : t('registrar_detail.history.empty_no_sites')}
        </TabEmpty>
      ) : (
        <>
          {/* The Registrar's own rows are already on screen; the others
              arrive one request at a time, so the table renders now and says
              the rest is still coming rather than blocking on all of them. */}
          {stillArriving ? (
            <p className="simsm-registrar-detail__muted" role="status" aria-live="polite">
              {t('registrar_detail.history.sites_loading')}
            </p>
          ) : null}
          <div className="simsm-registrar-detail__table">
            <MudTable
              className="simsm-table"
              columns={columns}
              rows={paged.slice.map((row) => ({ id: row.id }))}
              rowStyle="zebra"
              aria-label={t('registrar_detail.history.table_aria_label')}
            >
              {/* The date, and the time under it on the metadata line
                  (RegistratorDetalii.tsx:583-586). */}
              {paged.slice.map((row, index) => (
                <span
                  key={`when-${row.id}`}
                  slot={`cell-recordedAt-${index}`}
                  className="simsm-registrar-detail__stacked"
                >
                  <span>{formatDate(row.recordedAtIso)}</span>
                  <HistoryMeta
                    label={t('history.meta.label.time')}
                    value={formatTime(row.recordedAtIso)}
                  />
                </span>
              ))}
              {paged.slice.map((row, index) => (
                <span
                  key={`change-${row.id}`}
                  slot={`cell-change-${index}`}
                  className="simsm-registrar-detail__stacked"
                >
                  {row.site ? (
                    <HistoryMeta label={t('registrar_detail.history.site')} value={row.site} />
                  ) : null}
                  {row.credential ? (
                    <CredentialChange entry={row.credential} />
                  ) : (
                    <HistoryChanges entry={row.entry} />
                  )}
                  {/* TC-UC71-02-01: an edit an RSUD event made names the event. */}
                  {row.eventId && eventCaptionKey(row.attribution.source) ? (
                    <HistoryMeta
                      label={t(eventCaptionKey(row.attribution.source) as string)}
                      value={row.eventId}
                    />
                  ) : null}
                </span>
              ))}
              {/* The same renderer the history log and the history dialog use:
                  a named actor, an automatic source, or "not recorded" — never
                  "Sistem" for a row that simply predates the actor trail. */}
              {paged.slice.map((row, index) => (
                <span key={`actor-${row.id}`} slot={`cell-actor-${index}`}>
                  <HistoryActor entry={row.attribution} />
                </span>
              ))}
            </MudTable>
            <TableFooter
              total={rows.length}
              page={paged.page}
              pageSize={paged.pageSize}
              onPage={paged.setPage}
              onPageSize={paged.setPageSize}
            />
          </div>
        </>
      )}
    </div>
  );
}
